import type { ApiResponse, Place } from '@/types';

/**
 * Get API endpoint from environment variable
 */
function getApiEndpoint(): string {
  return import.meta.env.VITE_API_ENDPOINT || '';
}

/**
 * Fetch places data from the API
 * @returns Promise with the API response
 */
export async function fetchPlaces(): Promise<ApiResponse> {
  const endpoint = getApiEndpoint();

  if (!endpoint) {
    console.warn('API endpoint not configured. Using empty dataset.');
    return {
      places: [],
      success: true,
      message: 'API endpoint not configured',
    };
  }

  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json() as Place[];
    return {
      places: data,
      success: true,
      message: 'Places fetched successfully',
    };
  } catch (error) {
    console.error('Failed to fetch places:', error);
    return {
      places: [],
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Retry a fetch operation with exponential backoff
 */
export async function fetchWithRetry<T>(
  fetchFn: () => Promise<T>,
  maxRetries: number = 3,
  delayMs: number = 1000
): Promise<T> {
  let lastError: Error | null = null;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fetchFn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      if (i < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs * Math.pow(2, i)));
      }
    }
  }

  throw lastError || new Error('Fetch failed after retries');
}
